import type { WorkflowSelection } from '../workflows/components/workflowSelection';

import { WORK_CHAT_HOME_PATH } from './chat-home-path';

/**
 * Work-home query builders. Location (`view`, `routine`, `date`) lives in the
 * URL; Play / Add / Edit intent does not. Public alias is `routines`; `skills`
 * is the internal view name; `workflows` is the historical spelling.
 */

/** A Work-home view other than home itself, as `?view=` names it. */
export type WorkHomeView = 'discover' | 'skills' | 'calendar' | 'mail';

export type WorkHomePendingView =
  | 'home'
  | 'discover'
  | 'skills'
  | 'calendar'
  | 'mail'
  | 'create-routine'
  | 'new-chat'
  /** Home with the Tasks list expanded. A one-shot landing, never a URL view. */
  | 'tasks';

export type RoutineIntent = {
  id: string;
  autoRun?: boolean;
  runNonce?: number;
  autoAdd?: boolean;
  autoAdvanced?: boolean;
  firstRun?: boolean;
};

const LOCAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

type QueryValue = string | string[] | undefined;

function firstQueryValue(param: QueryValue): string | undefined {
  if (Array.isArray(param)) return param[0];
  return param;
}

/** `workflows` is the historical spelling of `skills`; `routines` is the public alias. */
export const parseWorkHomeViewParam = (param: QueryValue): WorkHomeView | null => {
  const value = firstQueryValue(param);
  switch (value) {
    case 'discover':
    case 'calendar':
    case 'mail':
    case 'skills':
      return value;
    case 'routines':
    case 'workflows':
      return 'skills';
    default:
      return null;
  }
};

export const parseWorkHomePendingView = (
  param: QueryValue
): WorkHomePendingView | null => {
  if (firstQueryValue(param) === 'create-routine') {
    return 'create-routine';
  }
  return parseWorkHomeViewParam(param);
};

/** Public `?view=` value. `home` / `new-chat` / `tasks` omit the param. */
export const serializeWorkHomeView = (view: WorkHomePendingView): string | undefined => {
  if (view === 'home' || view === 'new-chat' || view === 'tasks') return undefined;
  if (view === 'skills') return 'routines';
  return view;
};

export function locationFromSelection(
  selection: WorkflowSelection | null | undefined
): WorkflowSelection | null {
  if (!selection) return null;
  if (selection.kind === 'create') return { kind: 'create' };
  if (selection.kind === 'scheduled-run') {
    return { kind: 'scheduled-run', id: selection.id, date: selection.date };
  }
  return { kind: 'workflow', id: selection.id };
}

export const parseRoutineParam = (query: {
  routine?: QueryValue;
  date?: QueryValue;
}): WorkflowSelection | null => {
  const routine = firstQueryValue(query.routine);
  if (!routine) return null;
  if (routine === 'new') return { kind: 'create' };
  const date = firstQueryValue(query.date);
  if (date && LOCAL_DATE_RE.test(date)) {
    return { kind: 'scheduled-run', id: routine, date };
  }
  return { kind: 'workflow', id: routine };
};

export function workHomeHref({
  view = 'home',
  selection = null,
}: {
  view?: WorkHomePendingView;
  selection?: WorkflowSelection | null;
} = {}): string {
  const params = new URLSearchParams();
  const viewParam = serializeWorkHomeView(view);
  if (viewParam) params.set('view', viewParam);
  const location = view === 'skills' ? locationFromSelection(selection) : null;
  if (location?.kind === 'create') {
    params.set('routine', 'new');
  } else if (location?.kind === 'workflow') {
    params.set('routine', location.id);
  } else if (location?.kind === 'scheduled-run') {
    params.set('routine', location.id);
    params.set('date', location.date);
  }
  const qs = params.toString();
  return qs ? `${WORK_CHAT_HOME_PATH}?${qs}` : WORK_CHAT_HOME_PATH;
}

export function workHomeLocationFromUrl(url: string): {
  view: WorkHomePendingView;
  selection: WorkflowSelection | null;
} {
  const queryStart = url.indexOf('?');
  const hashStart = url.indexOf('#');
  const end = hashStart === -1 ? url.length : hashStart;
  const query =
    queryStart === -1 || queryStart > end ? '' : url.slice(queryStart + 1, end);
  const params = new URLSearchParams(query);
  const view = parseWorkHomePendingView(params.get('view') ?? undefined) ?? 'home';
  return {
    view,
    selection:
      view === 'skills'
        ? parseRoutineParam({
            routine: params.get('routine') ?? undefined,
            date: params.get('date') ?? undefined,
          })
        : null,
  };
}

export function parseWorkHomeViewFromUrl(url: string): WorkHomePendingView {
  return workHomeLocationFromUrl(url).view;
}

export function displayedWorkHomePath(path: string): string {
  const hashStart = path.indexOf('#');
  return hashStart === -1 ? path : path.slice(0, hashStart);
}

export function canonicalizeWorkHomeAsPath(path: string): string {
  const hashStart = path.indexOf('#');
  const hash = hashStart === -1 ? '' : path.slice(hashStart);
  const withoutHash = hashStart === -1 ? path : path.slice(0, hashStart);
  const queryStart = withoutHash.indexOf('?');
  const pathname = (
    queryStart === -1 ? withoutHash : withoutHash.slice(0, queryStart)
  ).replace(/\/+$/, '');
  if (pathname !== WORK_CHAT_HOME_PATH) return path;
  const params = new URLSearchParams(
    queryStart === -1 ? '' : withoutHash.slice(queryStart + 1)
  );
  const view = params.get('view');
  if (view !== 'skills' && view !== 'workflows') return path;
  params.set('view', 'routines');
  const qs = params.toString();
  return `${pathname}${qs ? `?${qs}` : ''}${hash}`;
}

export function isSameWorkHomeHref(asPath: string, href: string): boolean {
  return canonicalizeWorkHomeAsPath(asPath) === canonicalizeWorkHomeAsPath(href);
}

export function isWorkChatLandingPath(asPath: string): boolean {
  const path = (asPath.split(/[?#]/)[0] ?? '').replace(/\/+$/, '');
  return path === WORK_CHAT_HOME_PATH;
}

export function routineIntentFromSelection(
  selection: WorkflowSelection
): RoutineIntent | null {
  if (selection.kind !== 'workflow') return null;
  if (
    !selection.autoRun &&
    !selection.autoAdd &&
    !selection.autoAdvanced &&
    !selection.firstRun
  ) {
    return null;
  }
  return {
    id: selection.id,
    autoRun: selection.autoRun,
    runNonce: selection.runNonce,
    autoAdd: selection.autoAdd,
    autoAdvanced: selection.autoAdvanced,
    firstRun: selection.firstRun,
  };
}

export function mergeRoutineLocationWithIntent(
  location: WorkflowSelection | null,
  intent: RoutineIntent | null
): WorkflowSelection | null {
  if (!location) return null;
  if (location.kind !== 'workflow' || !intent || intent.id !== location.id) {
    return location;
  }
  return {
    kind: 'workflow',
    id: location.id,
    autoRun: intent.autoRun,
    runNonce: intent.runNonce,
    autoAdd: intent.autoAdd,
    autoAdvanced: intent.autoAdvanced,
    firstRun: intent.firstRun,
  };
}
